import { useCallback, useEffect, useRef, useState } from 'react';
import firebase from 'utils/firebase';
import server from 'utils/server';
import { buildSiaEmbedUrl } from 'utils/sia';

type SiaEmbedState = {
  iframeUrl: string | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

type UseSiaEmbedUrlOptions = {
  maxRetries?: number;
  retryDelayMs?: number;
  suppressErrorUntilRetriesExhausted?: boolean;
};

const ERROR_RESTAURANT = 'Restaurant-ID konnte nicht gefunden werden.';
const ERROR_AUTH = 'Benutzer ist nicht authentifiziert. Bitte erneut anmelden.';
const ERROR_LOAD = 'SIA konnte nicht geladen werden. Bitte versuche es erneut.';

const useSiaEmbedUrl = (
  restaurantId: string | null,
  theme: 'light' | 'dark',
  buildUrl: (restaurantId: string) => string = buildSiaEmbedUrl,
  options: UseSiaEmbedUrlOptions = {}
): SiaEmbedState => {
  const {
    maxRetries = 0,
    retryDelayMs = 1500,
    suppressErrorUntilRetriesExhausted = false,
  } = options;
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const retryTimeoutRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const clearRetryTimeout = useCallback(() => {
    if (retryTimeoutRef.current) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const waitForUser = useCallback((timeoutMs: number) => {
    return new Promise<any | null>((resolve) => {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        resolve(currentUser);
        return;
      }

      let timeoutId: number | null = null;

      const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }
        unsubscribe();
        resolve(user ?? null);
      });

      timeoutId = window.setTimeout(() => {
        unsubscribe();
        resolve(firebase.auth().currentUser ?? null);
      }, timeoutMs);
    });
  }, []);

  const fetchToken = useCallback(async (attempt = 0) => {
    clearRetryTimeout();

    if (!isMountedRef.current) return;

    if (attempt === 0) {
      setError(null);
    }

    const scheduleRetry = (message: string) => {
      if (!isMountedRef.current) return;

      if (attempt >= maxRetries) {
        setIframeUrl(null);
        setError(message);
        setLoading(false);
        return;
      }

      if (!suppressErrorUntilRetriesExhausted) {
        setError(message);
      } else {
        setError(null);
      }

      setLoading(true);
      retryTimeoutRef.current = window.setTimeout(() => {
        fetchToken(attempt + 1);
      }, retryDelayMs);
    };

    if (!restaurantId) {
      setIframeUrl(null);
      setLoading(false);
      setError(ERROR_RESTAURANT);
      return;
    }

    setLoading(true);

    let user = firebase.auth().currentUser;
    if (!user) {
      user = await waitForUser(Math.min(2000, retryDelayMs));
    }

    if (!isMountedRef.current) return;

    if (!user) {
      scheduleRetry(ERROR_AUTH);
      return;
    }

    try {
      const response = await server.get(
        `/v03/siaConnector/${restaurantId}/token`,
        {
          headers: { uid: user.uid },
        },
      );

      const token = response?.data?.token as string | undefined;
      const organizationId = response?.data?.organizationId as
        | string
        | undefined;

      if (!token) {
        throw new Error('Missing token in response.');
      }

      const url = `${buildUrl(
        organizationId || restaurantId
      )}?token=${encodeURIComponent(token)}&theme=${theme}`;

      if (!isMountedRef.current) return;

      setIframeUrl(url);
      setError(null);
      setLoading(false);
    } catch (err) {
      if (!isMountedRef.current) return;
      scheduleRetry(ERROR_LOAD);
    }
  }, [
    buildUrl,
    clearRetryTimeout,
    maxRetries,
    restaurantId,
    retryDelayMs,
    suppressErrorUntilRetriesExhausted,
    theme,
    waitForUser,
  ]);

  const refresh = useCallback(() => {
    fetchToken(0);
  }, [fetchToken]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchToken(0);

    return () => {
      isMountedRef.current = false;
      clearRetryTimeout();
    };
  }, [clearRetryTimeout, fetchToken]);

  return { iframeUrl, loading, error, refresh };
};

export default useSiaEmbedUrl;
